export type DiscountType = 'pct' | 'amount';

export interface DiscountLineInput {
  id:            string;
  quantity:      number;
  unitPrice:     number;
  discountType:  DiscountType;
  discountValue: number;
}

export interface DiscountLineResult {
  gross:         number;
  lineDiscount:  number;
  orderDiscount: number;
  net:           number;
}

export interface DiscountResult {
  lines:          Record<string, DiscountLineResult>;
  gross:          number;
  lineDiscounts:  number;
  orderDiscount:  number;
  totalDiscount:  number;
  net:            number;
}

const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

// Mirrors public.b2b_order_line_nets so the screen matches what the server saves.
export function computeOrderDiscounts(
  lines: DiscountLineInput[],
  orderType: DiscountType,
  orderValue: number,
): DiscountResult {
  const base = lines.map(l => {
    const qty = l.quantity || 0;
    const gross = r2(qty * l.unitPrice);
    const raw = l.discountType === 'amount'
      ? r2(Math.min(l.discountValue, l.unitPrice) * qty)
      : r2(gross * Math.min(l.discountValue, 100) / 100);
    const ld = Math.min(gross, Math.max(0, raw));
    return { id: l.id, gross, ld, after: r2(gross - ld) };
  });

  const afterTotal = r2(base.reduce((s, b) => s + b.after, 0));
  const orderDisc = orderType === 'amount'
    ? Math.min(r2(orderValue), afterTotal)
    : r2(afterTotal * Math.min(orderValue, 100) / 100);

  const shares = base.map(b =>
    orderDisc === 0 || afterTotal === 0 || b.after === 0 ? 0 : r2(orderDisc * b.after / afterTotal));
  const sorted = [...base].sort((a, b) =>
    (Number(b.after > 0) - Number(a.after > 0)) || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0));
  const remainderId = sorted[0] && sorted[0].after > 0 ? sorted[0].id : null;
  const sharesSum = shares.reduce((s, x) => s + x, 0);

  const result: Record<string, DiscountLineResult> = {};
  base.forEach((b, i) => {
    const own = shares[i] ?? 0;
    const share = b.id === remainderId ? r2(orderDisc - (sharesSum - own)) : own;
    result[b.id] = { gross: b.gross, lineDiscount: b.ld, orderDiscount: share, net: r2(b.after - share) };
  });

  const gross = r2(base.reduce((s, b) => s + b.gross, 0));
  const lineDiscounts = r2(base.reduce((s, b) => s + b.ld, 0));
  const net = r2(Object.values(result).reduce((s, l) => s + l.net, 0));
  return { lines: result, gross, lineDiscounts, orderDiscount: orderDisc, totalDiscount: r2(gross - net), net };
}
